import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  Ship,
  Hash,
  Boxes,
  LayoutGrid,
  Route as RouteIcon,
  History,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { ContainerRow, ContainerModalData } from '../interfaces/container.interface';
import {
  fmtDate,
  etaLabel,
} from '@/features/dashboard/data/claudeMockData';
import { useDirection } from '@/contexts/DirectionContext';
import {
  ALL_CONTAINER_STATUSES,
  containerStatusLabel,
} from '../constants/statuses';
import { formatEtaHeading, formatEtaSubtext } from '../constants/directionLabels';
import { useContainerModal } from '../hooks/useContainerModal';
import { ClientNoteEditor } from '@/components/claude/ClientNoteEditor';
import { useSaveClientNote } from '@/hooks/useClientNote';
import { ContainerJourneyTab } from './ContainerJourneyTab';
import { ContainerEventsTab } from './ContainerEventsTab';
import { visibleMovements } from '../utils/containerVoyage';
import { OvGroup, OvLongRow, OvRow, stripPortCode } from './OverviewGroups';
import { BillDocumentsPanel } from './BillDocumentsPanel';

interface ContainerViewModalProps {
  isOpen: boolean;
  container: ContainerRow | null;
  onClose: () => void;
}

type TabId = 'overview' | 'journey' | 'events' | 'docs';

export const ContainerViewModal: React.FC<ContainerViewModalProps> = ({
  isOpen,
  container,
  onClose,
}) => {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';
  const [tab, setTab] = useState<TabId>('overview');
  const { direction } = useDirection();
  // Documentele generate (Avizare de sosire, Cerere de eliberare, LOI) sunt
  // concepte de import, adresate consignee-ului la Constanța. Pe export clientul
  // e shipper, deci tab-ul nu are ce oferi — îl ascundem.
  const showDocsTab = direction !== 'export';

  const { data: modalData, isLoading } = useContainerModal(
    isOpen && container ? container.id : null,
  );

  useEffect(() => {
    if (isOpen) setTab('overview');
  }, [isOpen, container?.id]);

  // Dacă direcția se schimbă cât modalul e deschis pe tab-ul Documente,
  // revenim pe „Privire generală" ca să nu rămână un tab activ inexistent.
  useEffect(() => {
    if (!showDocsTab && tab === 'docs') setTab('overview');
  }, [showDocsTab, tab]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen || !container) return null;

  const eventCount = modalData ? visibleMovements(modalData, direction).length : null;
  const TABS: Array<{ id: TabId; labelRo: string; labelEn: string; icon: React.ElementType; count?: number | null }> = [
    { id: 'overview', labelRo: 'Privire generală', labelEn: 'Overview', icon: LayoutGrid },
    { id: 'journey', labelRo: 'Călătorie', labelEn: 'Journey', icon: RouteIcon },
    { id: 'events', labelRo: 'Evenimente', labelEn: 'Events', icon: History, count: eventCount },
    ...(showDocsTab
      ? [{ id: 'docs' as TabId, labelRo: 'Documente', labelEn: 'Documents', icon: FileText }]
      : []),
  ];
  const trackingUrl = container.containerNumber
    ? `https://www.yangming.com/en/esolution/cargo_tracking?service=${encodeURIComponent(
        container.containerNumber,
      )}`
    : null;

  const commodityShort = (s: string | null | undefined) =>
    s && s.length > 30 ? s.slice(0, 30) + '...' : s || '';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="modal-title">
              <span className="mono">{container.containerNumber || '—'}</span>
            </div>
            <div className="modal-sub">
              {container.containerType}
              {container.commodity ? ` · ${commodityShort(container.commodity)}` : ''}
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label={isRo ? 'Închide' : 'Close'}
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-tabs" role="tablist">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            return (
              <button
                key={tb.id}
                type="button"
                role="tab"
                aria-selected={tab === tb.id}
                className={'modal-tab' + (tab === tb.id ? ' active' : '')}
                onClick={() => setTab(tb.id)}
              >
                <Icon size={14} strokeWidth={2} />
                {isRo ? tb.labelRo : tb.labelEn}
                {tb.count != null && tb.count > 0 && <span className="modal-tab-count">{tb.count}</span>}
              </button>
            );
          })}
        </div>

        <div className="modal-body">
          {isLoading || !modalData ? (
            <div
              className="empty"
              style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div className="fw-600 fs-14">{isRo ? 'Se încarcă…' : 'Loading…'}</div>
            </div>
          ) : (
            <>
              {tab === 'overview' && <OverviewTab data={modalData} lang={lang} />}
              {tab === 'journey' && <ContainerJourneyTab data={modalData} lang={lang} />}
              {tab === 'events' && <ContainerEventsTab data={modalData} lang={lang} />}
              {tab === 'docs' && <DocsTab data={modalData} lang={lang} />}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-sm" onClick={onClose}>
            {isRo ? 'Închide' : 'Close'}
          </button>
          {trackingUrl && (
            <a
              className="btn btn-primary btn-sm"
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={13} />
              {isRo ? 'Deschide tracking complet' : 'Open full tracking'}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Overview
// ============================================================
const OverviewTab: React.FC<{ data: ContainerModalData; lang: 'ro' | 'en' }> = ({
  data,
  lang,
}) => {
  const isRo = lang === 'ro';
  const { direction } = useDirection();
  const isExport = direction === 'export';
  const st = ALL_CONTAINER_STATUSES[data.status];
  const { saveContainer } = useSaveClientNote();

  return (
    <div>
      {/* Header section */}
      <div
        className="ov-surface"
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)',
          padding: 18,
          marginBottom: 18,
        }}
      >
        <div
          className="row gap-3"
          style={{ justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 14 }}
        >
          <div className="row gap-3" style={{ flexWrap: 'wrap' }}>
            <span className="badge badge-neutral">{data.company.name || '—'}</span>
            {/* Pe import păstrăm comportamentul original: badge doar pentru un
                status cunoscut. Pe export afișăm și un fallback neutru. */}
            {(st || isExport) && (
              <span className={'badge ' + (st?.badge || 'badge-neutral')}>
                <span className="dot" />
                {containerStatusLabel(data.status, lang, data.rawStatus)}
              </span>
            )}
          </div>
        </div>

        {/* Route visualization */}
        <div className="ctn-route-row" style={{ alignItems: 'center', marginTop: 8 }}>
          <div className="ctn-route-pol">
            <div className="muted fs-11">POL</div>
            <div className="fw-600 fs-18" style={{ letterSpacing: '-0.015em' }}>
              {data.route.polName || data.route.pol || '—'}
            </div>
            <div className="muted fs-12 mono">{data.route.pol || ''}</div>
            <div className="muted fs-12 mt-2">ETD {fmtDate(data.route.etd, lang)}</div>
          </div>

          <div className="ctn-route-track" style={{ minWidth: 0 }}>
            <div style={{ position: 'relative', padding: '6px 0' }}>
              <div
                style={{
                  height: 3,
                  background: 'var(--surface-2)',
                  borderRadius: 99,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${Math.max(2, Math.min(100, data.progress * 100))}%`,
                    background: 'var(--accent)',
                    borderRadius: 99,
                    transition: 'width 0.3s',
                  }}
                />
                {data.progress > 0 && data.progress < 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: `${data.progress * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      background: 'var(--accent)',
                      color: 'var(--accent-fg)',
                      width: 26,
                      height: 26,
                      borderRadius: 99,
                      display: 'grid',
                      placeItems: 'center',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                      border: '2px solid var(--surface)',
                    }}
                  >
                    <Ship size={12} strokeWidth={2} />
                  </div>
                )}
              </div>
              <div
                className="row"
                style={{ justifyContent: 'space-between', marginTop: 8, fontSize: 11 }}
              >
                <span className="muted">
                  {Math.round(data.progress * 100)}% {isRo ? 'complet' : 'complete'}
                </span>
                <span className="muted">
                  {data.route.vesselName || '—'}
                  {data.route.vesselCode ? ` · ${data.route.vesselCode}` : ''}
                </span>
              </div>
            </div>
          </div>

          <div className="ctn-route-pod">
            <div className="muted fs-11">POD</div>
            <div className="fw-600 fs-18" style={{ letterSpacing: '-0.015em' }}>
              {data.route.podName || data.route.pod || '—'}
            </div>
            <div className="muted fs-12 mono">{data.route.pod || ''}</div>
            <div className="muted fs-12 mt-2">
              {isExport
                ? formatEtaHeading(direction, lang, {
                    ...data.route,
                    pod: data.route.pod,
                    podName: data.route.podName,
                  })
                : 'ETA'}{' '}
              {fmtDate(data.route.eta, lang)}
              {data.route.eta && !isExport && (
                <strong> · {etaLabel(data.route.eta, lang)}</strong>
              )}
              {data.route.eta && isExport && formatEtaSubtext(direction, lang, data.route) ? (
                <div className="muted fs-11 mt-1">
                  {formatEtaSubtext(direction, lang, data.route)}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Detalii pe grupuri — pe export afișăm doar câmpurile populate */}
      <div className="ov-groups">
        <OvGroup icon={Hash} title={isRo ? 'Referințe' : 'References'}>
          {(!isExport || data.booking.number) && (
            <OvRow label="Booking" value={data.booking.number} mono copy />
          )}
          {(!isExport || data.bls.length > 0) && (
            <OvRow
              label={data.bls.length > 1 ? (isRo ? 'Conosamente' : 'Bills of Lading') : isRo ? 'Conosament' : 'Bill of Lading'}
              value={data.bls.length ? data.bls.map((bl) => bl.code).join(', ') : null}
              mono
              copy
            />
          )}
          <OvRow label="Container" value={data.container.containerNumber} mono copy />
          {(!isExport || data.container.sealNumber) && (
            <OvRow label={isRo ? 'Sigiliu' : 'Seal'} value={data.container.sealNumber} mono copy />
          )}
        </OvGroup>

        <OvGroup icon={Ship} title="Transport">
          {(!isExport || data.route.feederName) && (
            <OvRow
              label={isExport ? (isRo ? 'Feeder' : 'Feeder') : isRo ? 'Navă' : 'Vessel'}
              value={(isExport ? data.route.feederName : data.route.vesselName) || null}
              sub={(isExport ? data.route.feederCode : data.route.vesselCode) || null}
            />
          )}
          {isExport && data.route.motherName && (
            <OvRow label={isRo ? 'Navă mamă' : 'Mother vessel'} value={data.route.motherName} sub={data.route.motherCode} />
          )}
          {isExport && data.route.lastFeederName && (
            <OvRow label={isRo ? 'Ultimul feeder' : 'Last feeder'} value={data.route.lastFeederName} sub={data.route.lastFeederCode} />
          )}
          {isExport && data.route.tsPort1 && (
            <OvRow
              label={data.route.tsPort2 ? 'Transshipment 1' : 'Transshipment'}
              value={stripPortCode(data.route.tsPort1Name) || data.route.tsPort1}
              sub={data.route.tsPort1Name ? data.route.tsPort1 : null}
            />
          )}
          {isExport && data.route.tsPort2 && (
            <OvRow
              label="Transshipment 2"
              value={stripPortCode(data.route.tsPort2Name) || data.route.tsPort2}
              sub={data.route.tsPort2Name ? data.route.tsPort2 : null}
            />
          )}
          {(!isExport || data.booking.onBoardDate) && (
            <OvRow label="On Board" value={data.booking.onBoardDate ? fmtDate(data.booking.onBoardDate, lang) : null} />
          )}
          {!isExport && <OvRow label={isRo ? 'Tip' : 'Shipping type'} value={data.booking.shippingType} />}
        </OvGroup>

        <OvGroup icon={Boxes} title={isRo ? 'Marfă' : 'Cargo'}>
          {(!isExport || data.container.commodity) && (
            <OvLongRow label={isRo ? 'Descriere' : 'Description'} value={data.container.commodity} isRo={isRo} />
          )}
          <OvRow label={isRo ? 'Tip container' : 'Container type'} value={data.container.containerType} />
          {(!isExport || (data.container.grossWeight && data.container.grossWeight > 0)) && (
            <OvRow
              label={isRo ? 'Greutate' : 'Weight'}
              value={
                data.container.grossWeight && data.container.grossWeight > 0
                  ? `${data.container.grossWeight.toLocaleString(isRo ? 'ro-RO' : 'en-GB')} kg`
                  : null
              }
              mono
            />
          )}
          {data.container.packageNumber != null && data.container.packageNumber > 0 && (
            <OvRow label={isRo ? 'Colete' : 'Packages'} value={String(data.container.packageNumber)} mono />
          )}
        </OvGroup>
      </div>

      <div style={{ marginTop: 18 }}>
        <ClientNoteEditor
          value={data.container.clientNote}
          onSave={(next) => saveContainer(data.container.id, next).then(() => undefined)}
          variant="block"
          lang={lang}
        />
      </div>
    </div>
  );
};

// ============================================================
// Docs — documentele se emit pe conosament (B/L); aici e doar o scurtătură
// ============================================================
const DocsTab: React.FC<{ data: ContainerModalData; lang: 'ro' | 'en' }> = ({ data, lang }) => {
  const isRo = lang === 'ro';
  const [billId, setBillId] = useState<number | null>(data.bls[0]?.id ?? null);
  const bill = data.bls.find((b) => b.id === billId) ?? data.bls[0] ?? null;

  return (
    <div>
      <div className="docs-bl-pick">
        <FileText size={14} strokeWidth={2} />
        <span>
          {isRo ? 'Documentele se emit pe conosament.' : 'Documents are issued per bill of lading.'}{' '}
          {data.bls.length > 1
            ? isRo
              ? 'Containerul e pe mai multe B/L-uri — alege unul:'
              : 'This container is on several B/Ls — pick one:'
            : bill
              ? isRo
                ? 'B/L:'
                : 'B/L:'
              : ''}
        </span>
        {data.bls.length > 1 ? (
          <span className="docs-bl-options">
            {data.bls.map((b) => (
              <button
                key={b.id}
                type="button"
                className={'ctn-chip' + (b.id === bill?.id ? ' active' : '')}
                onClick={() => setBillId(b.id)}
              >
                <span className="mono">{b.code}</span>
              </button>
            ))}
          </span>
        ) : (
          bill && <strong className="mono">{bill.code}</strong>
        )}
      </div>
      <BillDocumentsPanel
        key={bill?.id ?? 'none'}
        billId={bill?.id ?? null}
        releaseType={bill?.releaseType ?? null}
        status={data.status}
        lang={lang}
      />
    </div>
  );
};
